import { useEffect, useRef, useCallback, useState } from 'react';

interface LoadingState {
  isLoading: boolean;
  isSlow: boolean;
  isStuck: boolean;
  requestCount: number;
}

const SLOW_REQUEST_THRESHOLD = 20000; // 20 seconds
const STUCK_REQUEST_THRESHOLD = 45000; // 45 seconds

export function useLoadingMonitor() {
  const [loadingState, setLoadingState] = useState<LoadingState>({
    isLoading: false,
    isSlow: false,
    isStuck: false,
    requestCount: 0,
  });

  const activeRequestsRef = useRef<Map<string, number>>(new Map());
  const slowWarningShownRef = useRef<Set<string>>(new Set());
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Monitor active requests
  const checkActiveRequests = useCallback(() => {
    const now = Date.now();
    let slowCount = 0;
    let stuckCount = 0;

    activeRequestsRef.current.forEach((startTime, requestId) => {
      const duration = now - startTime;

      if (duration > STUCK_REQUEST_THRESHOLD) {
        stuckCount++;
        if (!slowWarningShownRef.current.has(requestId)) {
          console.error(`Request ${requestId} appears stuck (${(duration / 1000).toFixed(1)}s)`);
          slowWarningShownRef.current.add(requestId);
        }
      } else if (duration > SLOW_REQUEST_THRESHOLD) {
        slowCount++;
        if (!slowWarningShownRef.current.has(requestId)) {
          console.warn(`Slow request detected: ${requestId} (${(duration / 1000).toFixed(1)}s)`);
          slowWarningShownRef.current.add(requestId);
        }
      }
    });

    setLoadingState({
      isLoading: activeRequestsRef.current.size > 0,
      isSlow: slowCount > 0,
      isStuck: stuckCount > 0,
      requestCount: activeRequestsRef.current.size,
    });
  }, []);

  // Register a new request
  const registerRequest = useCallback((requestId: string) => {
    activeRequestsRef.current.set(requestId, Date.now());
    setLoadingState(prev => ({
      ...prev,
      isLoading: true,
      requestCount: activeRequestsRef.current.size,
    }));
  }, []);

  // Unregister a completed request
  const unregisterRequest = useCallback((requestId: string) => {
    const startTime = activeRequestsRef.current.get(requestId);
    if (startTime) {
      const duration = Date.now() - startTime;
      if (duration > 5000) {
        console.log(`Request ${requestId} completed in ${(duration / 1000).toFixed(1)}s`);
      }
    }

    activeRequestsRef.current.delete(requestId);
    slowWarningShownRef.current.delete(requestId);

    setLoadingState(prev => ({
      ...prev,
      isLoading: activeRequestsRef.current.size > 0,
      requestCount: activeRequestsRef.current.size,
    }));
  }, []);

  // Cancel a stuck request
  const cancelRequest = useCallback((requestId: string) => {
    console.log(`Manually canceling request: ${requestId}`);
    unregisterRequest(requestId);
  }, [unregisterRequest]);

  // Set up periodic monitoring
  useEffect(() => {
    checkIntervalRef.current = setInterval(checkActiveRequests, 5000);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [checkActiveRequests]);

  return {
    loadingState,
    registerRequest,
    unregisterRequest,
    cancelRequest,
  };
}

// Helper to wrap async operations with automatic monitoring
export function monitorAsync<T>(
  operation: Promise<T>,
  requestId: string,
  monitor: ReturnType<typeof useLoadingMonitor>
): Promise<T> {
  monitor.registerRequest(requestId);

  return operation
    .then(result => {
      monitor.unregisterRequest(requestId);
      return result;
    })
    .catch(error => {
      monitor.unregisterRequest(requestId);
      throw error;
    });
}
